/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description SavedSearchesView Selectors
 */

import { createSelector } from '@zcode/memoizers'
import { LISTABLES_API_LIMIT } from '@zcode/nr/es/shared/constants/list'
import type { State } from '@zcode/state-type'
import { compose } from 'redux'
import { NEARBY_THRESHOLD } from '../../../actions/list.action'
import { ALL_NEW_SAVED_SEARCHES_ID, ALL_SAVED_SEARCHES_ID } from '../constants/index'
import {
  selectHasListables,
  selectHasMoreListables,
  selectListables,
  selectMatching,
} from './listables'

const selectSavedSearchesView = (state: State) => state.savedSearchesView

export const selectIsLoadingListables = compose(
  (state) => state.fetchingListables,
  selectSavedSearchesView
)

export const selectSavedSearches = compose((state) => state.savedSearches, selectSavedSearchesView)

export const selectHasSavedSearches = compose(
  (state) => !!state.savedSearches?.length,
  selectSavedSearchesView
)

export const selectSelectedId = compose((state) => state.selectedId, selectSavedSearchesView)

export const selectAllFilters = compose((state) => state.allFilters, selectSavedSearchesView)

export const selectAllNewFilters = compose((state) => state.onlyNewFilters, selectSavedSearchesView)

export const selectAllCounts = compose((state) => state.allCounts, selectSavedSearchesView)

export const selectCounts = compose((state) => state.counts, selectSavedSearchesView)

export const selectHoveredId = compose((state) => state.hoveredId, selectSavedSearchesView)

export const selectImages = compose((state) => state.images, selectSavedSearchesView)

export const selectFetching = compose((state) => state.fetching, selectSavedSearchesView)

export const selectShowingSummary = compose(
  (state) => state.showingSummary,
  selectSavedSearchesView
)

export const selectSelected = createSelector(selectSelectedId, selectSavedSearches, (id, all) => {
  if (id === ALL_SAVED_SEARCHES_ID || id === ALL_NEW_SAVED_SEARCHES_ID) {
    return all
  }
  return all.find(({ searchId }) => searchId === id)
})

export const selectSavedSearchesFilters = createSelector(
  selectSelectedId,
  selectAllFilters,
  selectAllNewFilters,
  selectSelected,
  (id, all, allNew, selected) => {
    if (id === ALL_SAVED_SEARCHES_ID) {
      return {
        limit: LISTABLES_API_LIMIT,
        matching: true,
        name: 'All listings',
        queries: all,
        P: null,
        searchId: ALL_SAVED_SEARCHES_ID,
      }
    } else if (id === ALL_NEW_SAVED_SEARCHES_ID) {
      return {
        limit: LISTABLES_API_LIMIT,
        matching: true,
        name: 'New listings',
        queries: allNew,
        P: null,
        searchId: ALL_NEW_SAVED_SEARCHES_ID,
      }
    }
    if (selected && !Array.isArray(selected)) {
      return {
        ...selected.query,
        limit: LISTABLES_API_LIMIT,
        matching: true,
        name: selected.name,
        nearby: {
          threshold: NEARBY_THRESHOLD,
        },
        searchId: selected.searchId,
      }
    }
    return null
  }
)

export const selectHovered = createSelector(
  selectHoveredId,
  selectSavedSearches,
  (hoveredId, all) => all.find(({ searchId }) => searchId === hoveredId)
)

export const selectCurrentSearchLimit = compose((state) => state.limit, selectSavedSearchesView)

export const selectCurrentSearchOffset = compose((state) => state.offset, selectSavedSearchesView)

export const selectLoadMoreCount = compose((state) => state.loadMoreCount, selectSavedSearchesView)

export const selectReady = compose((state) => state.ready, selectSavedSearchesView)

export { selectHasListables, selectHasMoreListables, selectListables, selectMatching }
